/* eslint-disable */
import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import * as Store from '@apply/store';
import { stateSelectorValues, YearInput, ShowIf } from '@apply/ui';
import { useAuthentication } from '../../LoginPage';
import { alertModal } from '@apply/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { BusinessAndPeopleApi } from '@apply/api';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { LoadPanel } from 'devextreme-react/load-panel';
// import FileUploader from 'devextreme-react/file-uploader';

export interface EditBorrowerEntityModalProps {
    isOpen: boolean;
    onCloseCallback?: () => void;
    applicationInsights: ApplicationInsights;
    borrowerEntityId: any;
    selectedBusinessId: any;
}

export const EditBorrowerEntityModal: React.FC<EditBorrowerEntityModalProps> = ({
    isOpen,
    onCloseCallback,
    applicationInsights,
    borrowerEntityId,
    selectedBusinessId,
}) => {
    const businessAndPeopleApi = new BusinessAndPeopleApi.BusinessAndPeopleApi(applicationInsights);

    const { authentication, isFeatureEnabled } = useAuthentication();
    const [validation, set_validation] = useState(undefined);
    const [loadingMessage, set_loadingMessage] = useState('');
    const [loadingOpen, set_loadingOpen] = useState(false);
    const [businessId, set_businessId] = useState(0);
    const [borrowerEntity, set_borrowerEntity] = useState(null);
    const [canContinue, set_canContinue] = useState(true);
    const [companyId, set_companyId] = useState(null);
    const [name, set_name] = useState('');
    const [tin, set_tin] = useState('');
    const [entityType, set_entityType] = useState(null);
    const [yearIncorporated, set_yearIncorporated] = useState(null);
    const [stateIncorporated, set_stateIncorporated] = useState('');
    // const maxFileSize = (1024 * 1024 * 35);
    // const [organizationalFilesToUpload, set_organizationalFilesToUpload] = useState(null);
    // const [taxReturnFilesToUpload, set_taxReturnFilesToUpload] = useState(null);

    useEffect(() => {
        set_businessId(selectedBusinessId);
    }, [selectedBusinessId]);

    const loadDetails = async (id) => {
        set_loadingMessage('Loading...');
        set_loadingOpen(true);
        const response = await businessAndPeopleApi.getBorrowerEntityById(id);
        if (!response || response.statusCode !== 200) {
            return;
        }
        set_borrowerEntity(response.result);
    };

    useEffect(() => {
        async function borrowerEntityIdChanged() {
            resetState();
            if (!borrowerEntityId || borrowerEntityId <= 0) {
                set_borrowerEntity(null);
                return;
            }

            await loadDetails(borrowerEntityId);
            set_loadingOpen(false);
        }

        borrowerEntityIdChanged();
    }, [borrowerEntityId]);

    useEffect(() => {
        setState(borrowerEntity);
    }, [borrowerEntity]);

    useEffect(() => {
        let fieldsValid = false;

        fieldsValid = name !== '' && tin !== '' && stateIncorporated !== '';

        set_canContinue(fieldsValid);
    }, [name, tin, stateIncorporated, entityType, yearIncorporated]);

    const setState = (borrowerEntity) => {
        if (!borrowerEntity) {
            return;
        }
        set_companyId(borrowerEntity.companyId);
        set_name(borrowerEntity.name);
        set_tin(borrowerEntity.tin);
        set_entityType(borrowerEntity.entityType);
        set_yearIncorporated(borrowerEntity.yearIncorporated);
        set_stateIncorporated(borrowerEntity.stateIncorporated);
    };

    const resetState = () => {
        set_companyId(null);
        set_name('');
        set_tin('');
        set_entityType(null);
        set_yearIncorporated(null);
        set_stateIncorporated('');
    };

    const getSuccessMessage = (id) => {
        if (id === 0) {
            return 'Your Borrower Entity was added successfully.';
        } else {
            return 'Your Borrower Entity was updated successfully';
        }
    };

    const getGeneralErrorMessage = () => {
        return 'An error has occurred while communicating with our server.  Please try your operation again.';
    };

    const formatTin = (tin) => {
        return !tin
            ? ''
            : tin.length > 11
              ? tin.substr(0, 11)
              : tin
                    .replace(/[^\d*]/g, '')
                    .replace(/^(.{1,2})(.{1,7})?.*$/, (_, x, y, z) => x + (y ? `-${y}` : '') + (z ? `-${z}` : ''));
    };

    // const onOrganizationFilesChanged = (fileArray) => {
    //   const files = fileArray.filter(file => file.size <= maxFileSize);
    //   if (files && files.length > 0){
    //     set_canContinue(true);
    //   }
    //   else{
    //     set_canContinue(false);
    //   }
    //   set_organizationalFilesToUpload(files);
    // };

    // const onTaxReturnFilesChanged = (fileArray) => {
    //   const files = fileArray.filter(file => file.size <= maxFileSize);
    //   if (files && files.length > 0){
    //     set_canContinue(true);
    //   }
    //   else{
    //     set_canContinue(false);
    //   }
    //   set_taxReturnFilesToUpload(files);
    // };

    const handleClose = () => {
        set_borrowerEntity(null);
        set_validation(undefined);
        resetState();
        onCloseCallback(null);
    };

    const cancelBorrowerEntity = () => {
        set_borrowerEntity(null);
        set_validation(undefined);
        resetState();
        onCloseCallback(null);
    };

    const validateBorrowerEntity = async () => {
        set_loadingMessage('Validating...');
        set_loadingOpen(true);
        set_validation(undefined);
        try {
            const model = {
                entityId: borrowerEntityId,
                name,
                tin,
                state: stateIncorporated,
            };

            const response = await businessAndPeopleApi.validateBorrowerEntity(model);
            if (response.statusCode !== 200 || !response.result) {
                set_loadingOpen(false);
                return false;
            }

            if (response.result.hasErrors || response.result.hasWarnings) {
                set_validation(response.result);
                set_loadingOpen(false);
                return false;
            }

            return true;
        } catch (ex) {
            set_loadingOpen(false);
            set_validation(undefined);
            alertModal({
                message: getGeneralErrorMessage(),
            });
            return false;
        }
    };

    const saveBorrowerEntity = async () => {
        try {
            const isValid = await validateBorrowerEntity();
            if (!isValid) {
                return;
            }

            const model = {
                entityId: borrowerEntityId ?? 0,
                companyId: companyId ?? businessId,
                name,
                entityType,
                yearIncorporated: yearIncorporated ? new Date(yearIncorporated).toISOString().split('T')[0] : null,
                stateIncorporated,
                tin,
            };
            const response = await businessAndPeopleApi.saveBorrowerEntity(model);
            if (response.statusCode !== 200 || !response.result) {
                set_loadingOpen(false);
                alert(response.errors[0]);
                return false;
            }

            set_loadingOpen(false);
            alertModal({
                title: 'Success',
                message: getSuccessMessage(model.entityId),
                onConfirm: () => {
                    handleClose();
                },
            });
        } catch (ex) {
            set_loadingOpen(false);
            set_validation(undefined);
            alertModal({
                message: getGeneralErrorMessage(),
            });
            return false;
        }
    };

    const onYearSelection = (value) => {
        if (value && value !== '' && value != null) {
            let selectedDate = new Date(value).toISOString().split('T')[0];
            set_yearIncorporated(selectedDate);
        } else {
            set_yearIncorporated(null);
        }
    };

    const styles = {
        button: {
            width: 150,
        },
        noPadding: {
            padding: 0,
        },
        checkbox: {
            marginRight: '5px',
        },
    };

    return (
        <div>
            <Dialog
                open={isOpen}
                aria-labelledby='alert-dialog-title'
                aria-describedby='alert-dialog-description'
                maxWidth='md'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            role='dialog'
                            style={{ width: '500px', maxWidth: '80vw' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1>
                                        {!borrowerEntityId && borrowerEntityId === 0
                                            ? 'Add Borrower Entity'
                                            : 'Edit Borrower Entity'}
                                    </h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <div className='modal-content '>
                                    <div className='modal-body'>
                                        <div
                                            className='standard-form'
                                            style={styles.noPadding}
                                        >
                                            <div
                                                className='scrollable-content'
                                                style={{ margin: '10px' }}
                                            >
                                                <ShowIf showIf={validation?.hasErrors}>
                                                    {validation?.messages?.map((m) => {
                                                        return (
                                                            <div className='row'>
                                                                <div className='columns small-12 large-12'>
                                                                    <div>
                                                                        <span
                                                                            style={{
                                                                                color: 'red',
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faExclamationTriangle}
                                                                                style={{
                                                                                    color: 'red',
                                                                                }}
                                                                            />
                                                                            &nbsp;
                                                                            <span
                                                                                dangerouslySetInnerHTML={{
                                                                                    __html: m.message,
                                                                                }}
                                                                            />
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </ShowIf>
                                                <div className='row'>
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='name'>
                                                            Legal Entity Name <span className='req'>*</span>
                                                        </label>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Please provide the name of the Entity.</span>
                                                        </span>
                                                        <input
                                                            id='name'
                                                            type='text'
                                                            maxLength={100}
                                                            value={name}
                                                            onChange={(event) => set_name(event.target.value)}
                                                            required={true}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='tin'>
                                                            Tax Identification Number <span className='req'>*</span>
                                                        </label>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>
                                                                Your Employer identification Number or Tax Identification Number.
                                                                <br />
                                                                <br />
                                                                e.g. XX-XXXXXXX
                                                            </span>
                                                        </span>
                                                        <input
                                                            type='text'
                                                            maxLength={11}
                                                            id='tin'
                                                            value={tin}
                                                            onChange={(e) => set_tin(formatTin(e.target.value))}
                                                            required={true}
                                                        />
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='entityType'>Entity Type</label>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Please provide the type of the Entity.</span>
                                                        </span>
                                                        <select
                                                            id='entityType'
                                                            value={entityType}
                                                            onChange={(e) => set_entityType(parseInt(e.target.value))}
                                                        >
                                                            <option
                                                                key=''
                                                                value=''
                                                            ></option>
                                                            {Store.BorrowerStore.Types.entityTypes &&
                                                                Store.BorrowerStore.Types.entityTypes.map((et) => {
                                                                    return (
                                                                        <option
                                                                            key={et.id}
                                                                            value={et.id}
                                                                        >
                                                                            {et.name}
                                                                        </option>
                                                                    );
                                                                })}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className='row'>
                                                    <div className='columns small-12 large-6'>
                                                        <label htmlFor='stateIncorporated'>
                                                            State of Incorporation <span className='req'>*</span>
                                                        </label>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>
                                                                Please select state from which your business license was issued.
                                                            </span>
                                                        </span>
                                                        <select
                                                            id={'stateIncorporated'}
                                                            value={stateIncorporated ?? ''}
                                                            onChange={(event) => set_stateIncorporated(event.target.value)}
                                                            required={true}
                                                        >
                                                            {stateSelectorValues.map((v, i) => {
                                                                return (
                                                                    <option
                                                                        key={i}
                                                                        value={v.value}
                                                                    >
                                                                        {v.display}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                    <div className='columns small-12 large-6'>
                                                        <label htmlFor='yearIncorporated'>Year of Incorporation</label>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>
                                                                Please select year your Entity was Incorporated.
                                                            </span>
                                                        </span>
                                                        <YearInput
                                                            id='yearIncorporated'
                                                            value={
                                                                yearIncorporated
                                                                    ? new Date(yearIncorporated).getUTCFullYear()
                                                                    : null
                                                            }
                                                            onChange={(event) => onYearSelection(event.target.value)}
                                                            maxWidth='190px'
                                                        />
                                                    </div>
                                                </div>
                                                {/* <div className="row">
                            <div className="columns small-12 large-12">
                                <label htmlFor="unitCount">
                                    Organizational Documents
                                </label>
                                <span className="helper">
                                    <span className="tooltip-trigger">
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </span>
                                    <span className="text">
                                        Upload the organizational documents for this Entity
                                    </span>
                                </span>
                                <div style={{border: '1px solid lightgray'}}>
                                    <FileUploader
                                        id="organizationalFiles"
                                        selectButtonText="Select Files"
                                        uploadMode="useForm"
                                        maxFileSize={maxFileSize}
                                        invalidFileExtensionMessage="Invalid File Type. File will be ignored"
                                        invalidMaxFileSizeMessage="File too large. File will be ignored"
                                        multiple={true}
                                        allowedFileExtensions={['.pdf', '.png', '.jpg']}
                                        allowCanceling={true}
                                        labelText="or Drag & Drop the file here"
                                        onValueChange={onOrganizationFilesChanged}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="columns small-12 large-12">
                                <label>
                                    Entity Tax Returns (Past Two Years)
                                </label>
                                <span className="helper">
                                    <span className="tooltip-trigger">
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </span>
                                    <span className="text">
                                        Upload the Entity's tax returns for the past two years
                                    </span>
                                </span>
                                <div style={{border: '1px solid lightgray'}}>
                                    <FileUploader
                                        id="taxReturnFiles"
                                        selectButtonText="Select Files"
                                        uploadMode="useForm"
                                        maxFileSize={maxFileSize}
                                        invalidFileExtensionMessage="Invalid File Type. File will be ignored"
                                        invalidMaxFileSizeMessage="File too large. File will be ignored"
                                        multiple={true}
                                        allowedFileExtensions={['.pdf', '.png', '.jpg']}
                                        allowCanceling={true}
                                        labelText="or Drag & Drop the file here"
                                        onValueChange={onTaxReturnFilesChanged}
                                    />
                                </div>
                            </div>
                        </div> */}
                                            </div>
                                            <div className='modal-content-actions'>
                                                <button
                                                    type='button'
                                                    data-testid='accept-button'
                                                    className='button primary'
                                                    style={styles.button}
                                                    onClick={cancelBorrowerEntity}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type='button'
                                                    data-testid='accept-button'
                                                    className='button primary'
                                                    style={styles.button}
                                                    onClick={saveBorrowerEntity}
                                                    disabled={!canContinue}
                                                >
                                                    Save
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions></DialogActions>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
        </div>
    );
};
